// M09 多項式、餘式及因式定理 - 主題 11-20 數據文件
// Polynomials, Remainder and Factor Theorem - Topics 11-20
// 系統化設計，準備後續 Supabase 數據遷移

// M09-11: 特殊解分析 (Special Solution Analysis)
export const M09_TOPICS_11_TO_20 = [
  {
    id: 'M09_11',
    name: '特殊解分析',
    description: '分析多項式方程的特殊解與性質',
    category: 'special_solution_analysis',
    difficulty: 'advanced'
  },
  {
    id: 'M09_12',
    name: '重根條件',
    description: '多項式重根的判定與應用',
    category: 'multiple_root_conditions',
    difficulty: 'intermediate'
  },
  {
    id: 'M09_13',
    name: '待定係數',
    description: '利用待定係數法確定多項式',
    category: 'undetermined_coefficients',
    difficulty: 'intermediate'
  },
  {
    id: 'M09_14',
    name: '係數比較',
    description: '通過係數比較確定多項式參數',
    category: 'coefficient_comparison',
    difficulty: 'intermediate'
  },
  {
    id: 'M09_15',
    name: '直接代入',
    description: '直接代入法在多項式計算中的應用',
    category: 'direct_substitution',
    difficulty: 'basic'
  },
  {
    id: 'M09_16',
    name: '有理根定理',
    description: '有理根定理的應用與證明',
    category: 'rational_root_theorem',
    difficulty: 'advanced'
  },
  {
    id: 'M09_17',
    name: '多項式構造',
    description: '根據條件構造多項式',
    category: 'polynomial_construction',
    difficulty: 'advanced'
  },
  {
    id: 'M09_18',
    name: '多項式插值',
    description: '拉格朗日插值與牛頓插值',
    category: 'polynomial_interpolation',
    difficulty: 'advanced'
  },
  {
    id: 'M09_19',
    name: '多項式除法算法',
    description: '各種多項式除法算法的比較',
    category: 'polynomial_division_algorithms',
    difficulty: 'intermediate'
  },
  {
    id: 'M09_20',
    name: '多項式恆等',
    description: '多項式恆等式的證明與應用',
    category: 'polynomial_identities',
    difficulty: 'advanced'
  }
];

// M09 問題數據 - 主題 11-20
export const M09_PROBLEMS_11_TO_20 = [
  // M09-11: 特殊解分析
  {
    id: 'M09_11_01',
    topicId: 'M09_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '分析多項式的特殊解：',
    parts: {
      a: {
        question: '已知多項式 $P(x) = x^3 - 6x^2 + 11x - 6$ 有整數根，求所有整數根',
        solution: {
          steps: [
            '根據有理根定理，可能的整數根是常數項的因數：$\\pm 1, \\pm 2, \\pm 3, \\pm 6$',
            '檢驗 $x = 1$：$P(1) = 1 - 6 + 11 - 6 = 0$ ✓',
            '檢驗 $x = 2$：$P(2) = 8 - 24 + 22 - 6 = 0$ ✓',
            '檢驗 $x = 3$：$P(3) = 27 - 54 + 33 - 6 = 0$ ✓',
            '因式分解驗證：$P(x) = (x-1)(x-2)(x-3)$'
          ],
          answer: '整數根為 $x = 1, 2, 3$'
        },
        hint: '利用有理根定理，檢驗常數項的因數'
      },
      b: {
        question: '若多項式 $Q(x) = x^4 - 10x^2 + 9$ 可以因式分解，求其所有根',
        solution: {
          steps: [
            '設 $y = x^2$，則 $Q(x) = y^2 - 10y + 9$',
            '因式分解：$y^2 - 10y + 9 = (y-1)(y-9)$',
            '所以 $Q(x) = (x^2-1)(x^2-9) = (x-1)(x+1)(x-3)(x+3)$',
            '因此根為 $x = \\pm 1, \\pm 3$'
          ],
          answer: '根為 $x = 1, -1, 3, -3$'
        },
        hint: '利用換元法，設 $y = x^2$ 簡化問題'
      },
      c: {
        question: '分析多項式 $R(x) = x^3 + px + q$ 何時有三個不同實根',
        solution: {
          steps: [
            '對於三次多項式 $R(x) = x^3 + px + q$，判別式為 $\\Delta = -4p^3 - 27q^2$',
            '三個不同實根的條件是 $\\Delta > 0$',
            '即 $-4p^3 - 27q^2 > 0$，化簡為 $4p^3 + 27q^2 < 0$',
            '由於 $27q^2 \\geq 0$，需要 $4p^3 < -27q^2 \\leq 0$，所以 $p < 0$',
            '具體條件：$p < 0$ 且 $4p^3 + 27q^2 < 0$'
          ],
          answer: '當 $p < 0$ 且 $4p^3 + 27q^2 < 0$ 時有三個不同實根'
        },
        hint: '利用三次方程的判別式分析根的性質'
      }
    }
  },
  {
    id: 'M09_11_02',
    topicId: 'M09_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '多項式的對稱根：',
    parts: {
      a: {
        question: '已知多項式 $P(x) = x^4 + ax^3 + bx^2 + ax + 1$ 的根具有對稱性，分析其根的特點',
        solution: {
          steps: [
            '注意到係數具有對稱性：$P(x) = x^4 + ax^3 + bx^2 + ax + 1$',
            '如果 $r$ 是根，則 $P(r) = 0$',
            '考慮 $x^4 P(\\frac{1}{x}) = x^4 \\cdot (\\frac{1}{x^4} + \\frac{a}{x^3} + \\frac{b}{x^2} + \\frac{a}{x} + 1)$',
            '$= 1 + ax + bx^2 + ax^3 + x^4 = P(x)$',
            '這表明如果 $r$ 是根，則 $\\frac{1}{r}$ 也是根'
          ],
          answer: '根成對出現：如果 $r$ 是根，則 $\\frac{1}{r}$ 也是根'
        },
        hint: '利用多項式係數的對稱性分析根的對稱性'
      },
      b: {
        question: '求多項式 $P(x) = x^4 - 5x^3 + 6x^2 - 5x + 1$ 的根',
        solution: {
          steps: [
            '由對稱性知，如果 $r$ 是根，則 $\\frac{1}{r}$ 也是根',
            '設四個根為 $r_1, \\frac{1}{r_1}, r_2, \\frac{1}{r_2}$',
            '利用韋達定理：$r_1 + \\frac{1}{r_1} + r_2 + \\frac{1}{r_2} = 5$',
            '設 $s_1 = r_1 + \\frac{1}{r_1}$，$s_2 = r_2 + \\frac{1}{r_2}$，則 $s_1 + s_2 = 5$',
            '又 $r_1 \\cdot \\frac{1}{r_1} \\cdot r_2 \\cdot \\frac{1}{r_2} = 1$ ✓',
            '通過進一步計算可得根'
          ],
          answer: '需要進一步的代數計算確定具體根'
        },
        hint: '利用對稱根的性質和韋達定理'
      },
      c: {
        question: '證明：如果多項式 $a_nx^n + a_{n-1}x^{n-1} + \\cdots + a_1x + a_0$ 的係數滿足 $a_k = a_{n-k}$，則根具有倒數對稱性',
        solution: {
          steps: [
            '設 $P(x) = a_nx^n + a_{n-1}x^{n-1} + \\cdots + a_1x + a_0$',
            '由係數對稱性：$a_k = a_{n-k}$ 對所有 $k$ 成立',
            '考慮 $x^n P(\\frac{1}{x}) = x^n \\sum_{k=0}^n a_k (\\frac{1}{x})^k = \\sum_{k=0}^n a_k x^{n-k}$',
            '由對稱性：$\\sum_{k=0}^n a_k x^{n-k} = \\sum_{k=0}^n a_{n-k} x^k = P(x)$',
            '因此 $x^n P(\\frac{1}{x}) = P(x)$，即如果 $P(r) = 0$，則 $P(\\frac{1}{r}) = 0$'
          ],
          answer: '證明完成：係數對稱的多項式具有倒數對稱根'
        },
        hint: '利用係數對稱性和多項式變換'
      }
    }
  },
  {
    id: 'M09_11_03',
    topicId: 'M09_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '多項式的週期性根：',
    parts: {
      a: {
        question: '求多項式 $P(x) = x^3 - 1$ 的所有根',
        solution: {
          steps: [
            '因式分解：$x^3 - 1 = (x-1)(x^2+x+1)$',
            '第一個根：$x = 1$',
            '求解 $x^2 + x + 1 = 0$：$x = \\frac{-1 \\pm \\sqrt{1-4}}{2} = \\frac{-1 \\pm i\\sqrt{3}}{2}$',
            '設 $\\omega = \\frac{-1 + i\\sqrt{3}}{2}$，則另一根為 $\\omega^2 = \\frac{-1 - i\\sqrt{3}}{2}$',
            '驗證：$\\omega^3 = 1$，$\\omega^2 + \\omega + 1 = 0$'
          ],
          answer: '根為 $1, \\omega, \\omega^2$，其中 $\\omega = \\frac{-1 + i\\sqrt{3}}{2}$'
        },
        hint: '三次單位根具有特殊的性質'
      },
      b: {
        question: '分析 $\\omega = \\frac{-1 + i\\sqrt{3}}{2}$ 的性質',
        solution: {
          steps: [
            '計算 $\\omega^2 = (\\frac{-1 + i\\sqrt{3}}{2})^2 = \\frac{1 - 2i\\sqrt{3} - 3}{4} = \\frac{-2 - 2i\\sqrt{3}}{4} = \\frac{-1 - i\\sqrt{3}}{2}$',
            '驗證 $\\omega^3 = \\omega \\cdot \\omega^2 = \\frac{-1 + i\\sqrt{3}}{2} \\cdot \\frac{-1 - i\\sqrt{3}}{2} = \\frac{1 + 3}{4} = 1$',
            '驗證 $1 + \\omega + \\omega^2 = 1 + \\frac{-1 + i\\sqrt{3}}{2} + \\frac{-1 - i\\sqrt{3}}{2} = 1 - 1 = 0$',
            '$\\omega$ 是原始三次單位根'
          ],
          answer: '$\\omega$ 是原始三次單位根，滿足 $\\omega^3 = 1$ 且 $1 + \\omega + \\omega^2 = 0$'
        },
        hint: '三次單位根具有週期性和對稱性'
      },
      c: {
        question: '利用三次單位根分解 $x^3 + y^3$',
        solution: {
          steps: [
            '由於 $\\omega^3 = 1$，有 $x^3 + y^3 = x^3 - (-y^3) = x^3 - ((-y)^3)$',
            '設 $z = -y$，則 $x^3 + y^3 = x^3 - z^3$',
            '利用 $a^3 - b^3 = (a-b)(a^2+ab+b^2)$',
            '但更直接的方法：$x^3 + y^3 = (x+y)(x^2-xy+y^2)$',
            '或者利用單位根：$x^3 + y^3 = (x+y)(x+\\omega y)(x+\\omega^2 y)$'
          ],
          answer: '$x^3 + y^3 = (x+y)(x+\\omega y)(x+\\omega^2 y)$'
        },
        hint: '利用三次單位根可以完全分解三次齊次多項式'
      }
    }
  },
  {
    id: 'M09_11_04',
    topicId: 'M09_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '多項式的極值分析：',
    parts: {
      a: {
        question: '已知多項式 $P(x) = x^4 - 4x^3 + 6x^2 - 4x + 1$，求其極值點',
        solution: {
          steps: [
            '求導：$P\'(x) = 4x^3 - 12x^2 + 12x - 4 = 4(x^3 - 3x^2 + 3x - 1)$',
            '注意到 $x^3 - 3x^2 + 3x - 1 = (x-1)^3$',
            '所以 $P\'(x) = 4(x-1)^3$',
            '令 $P\'(x) = 0$：$(x-1)^3 = 0$，得 $x = 1$（三重根）',
            '由於是奇數重根，$x = 1$ 是拐點而非極值點'
          ],
          answer: '$P(x)$ 沒有極值點，$x = 1$ 是拐點'
        },
        hint: '注意區分極值點和拐點'
      },
      b: {
        question: '分析 $P(x) = x^4 - 4x^3 + 6x^2 - 4x + 1$ 的圖像性質',
        solution: {
          steps: [
            '注意到 $P(x) = (x-1)^4$（可以驗證展開）',
            '這是一個四次方，頂點在 $(1, 0)$',
            '由於是偶次方，$P(x) \\geq 0$ 對所有 $x$ 成立',
            '在 $x = 1$ 處取得最小值 $0$',
            '函數圖像是向上開口的拋物線型，在 $x = 1$ 處與 $x$ 軸相切'
          ],
          answer: '$P(x) = (x-1)^4$，在 $x = 1$ 處取得全局最小值 $0$'
        },
        hint: '識別完全四次方的形式'
      },
      c: {
        question: '求多項式 $Q(x) = x^5 - 5x^3 + 4x$ 的極值',
        solution: {
          steps: [
            '求導：$Q\'(x) = 5x^4 - 15x^2 + 4 = 5x^4 - 15x^2 + 4$',
            '令 $Q\'(x) = 0$：$5x^4 - 15x^2 + 4 = 0$',
            '設 $y = x^2$：$5y^2 - 15y + 4 = 0$',
            '使用公式法：$y = \\frac{15 \\pm \\sqrt{225-80}}{10} = \\frac{15 \\pm \\sqrt{145}}{10}$',
            '得到 $x^2 = \\frac{15 \\pm \\sqrt{145}}{10}$，進而求得 $x$ 值',
            '檢查二階導數確定極值性質'
          ],
          answer: '需要進一步計算確定具體的極值點和極值'
        },
        hint: '利用換元法求解四次方程'
      }
    }
  },
  {
    id: 'M09_11_05',
    topicId: 'M09_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '多項式的漸近行為：',
    parts: {
      a: {
        question: '分析多項式 $P(x) = 2x^3 - 3x^2 + x - 1$ 當 $x \\to +\\infty$ 和 $x \\to -\\infty$ 時的行為',
        solution: {
          steps: [
            '當 $x \\to +\\infty$ 時，主導項是 $2x^3$',
            '由於最高次項係數為正，$P(x) \\to +\\infty$',
            '當 $x \\to -\\infty$ 時，$2x^3 \\to -\\infty$（奇次方）',
            '因此 $P(x) \\to -\\infty$'
          ],
          answer: '$\\lim_{x \\to +\\infty} P(x) = +\\infty$，$\\lim_{x \\to -\\infty} P(x) = -\\infty$'
        },
        hint: '多項式的漸近行為由最高次項決定'
      },
      b: {
        question: '求 $P(x) = 2x^3 - 3x^2 + x - 1$ 與 $x$ 軸的交點個數',
        solution: {
          steps: [
            '由於 $P(x)$ 是三次多項式且 $\\lim_{x \\to +\\infty} P(x) = +\\infty$，$\\lim_{x \\to -\\infty} P(x) = -\\infty$',
            '由中間值定理，至少有一個實根',
            '求導：$P\'(x) = 6x^2 - 6x + 1$',
            '判別式：$\\Delta = 36 - 24 = 12 > 0$，有兩個不同實根',
            '這意味著 $P(x)$ 有兩個極值點，結合端點行為，恰好有一個實根'
          ],
          answer: '$P(x)$ 與 $x$ 軸恰好有一個交點'
        },
        hint: '結合導數分析和端點行為判斷根的個數'
      },
      c: {
        question: '估計 $P(x) = 2x^3 - 3x^2 + x - 1$ 的實根的大致範圍',
        solution: {
          steps: [
            '計算一些函數值：$P(0) = -1 < 0$',
            '$P(1) = 2 - 3 + 1 - 1 = -1 < 0$',
            '$P(2) = 16 - 12 + 2 - 1 = 5 > 0$',
            '由中間值定理，實根在 $(1, 2)$ 區間內',
            '進一步細分：$P(1.5) = 2(1.5)^3 - 3(1.5)^2 + 1.5 - 1 = 6.75 - 6.75 + 0.5 = 0.5 > 0$',
            '所以實根在 $(1, 1.5)$ 區間內'
          ],
          answer: '實根大約在區間 $(1, 1.5)$ 內'
        },
        hint: '利用中間值定理和函數值計算縮小根的範圍'
      }
    }
  },

  // M09-12: 重根條件
  {
    id: 'M09_12_01',
    topicId: 'M09_12',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '多項式重根的判定：',
    parts: {
      a: {
        question: '判斷多項式 $P(x) = x^3 - 3x^2 + 3x - 1$ 是否有重根',
        solution: {
          steps: [
            '求導：$P\'(x) = 3x^2 - 6x + 3 = 3(x^2 - 2x + 1) = 3(x-1)^2$',
            '多項式有重根當且僅當 $P(x)$ 和 $P\'(x)$ 有公共根',
            '令 $P\'(x) = 0$：$(x-1)^2 = 0$，得 $x = 1$',
            '檢查 $P(1) = 1 - 3 + 3 - 1 = 0$',
            '由於 $P(1) = 0$ 且 $P\'(1) = 0$，$x = 1$ 是重根'
          ],
          answer: '$P(x)$ 有重根 $x = 1$'
        },
        hint: '重根是使多項式及其導數同時為零的根'
      },
      b: {
        question: '確定 $P(x) = x^3 - 3x^2 + 3x - 1$ 中 $x = 1$ 的重數',
        solution: {
          steps: [
            '已知 $P(1) = 0$，$P\'(1) = 0$',
            '計算二階導數：$P\'\'(x) = 6x - 6$',
            '$P\'\'(1) = 6 - 6 = 0$',
            '計算三階導數：$P\'\'\'(x) = 6$',
            '$P\'\'\'(1) = 6 \\neq 0$',
            '因此 $x = 1$ 是三重根'
          ],
          answer: '$x = 1$ 是三重根'
        },
        hint: '重數等於第一個非零導數的階數'
      },
      c: {
        question: '驗證 $P(x) = x^3 - 3x^2 + 3x - 1 = (x-1)^3$',
        solution: {
          steps: [
            '展開 $(x-1)^3$ 使用二項式定理：',
            '$(x-1)^3 = x^3 - 3x^2(1) + 3x(1)^2 - (1)^3$',
            '$= x^3 - 3x^2 + 3x - 1$',
            '這確實等於給定的多項式 $P(x)$',
            '因此 $P(x) = (x-1)^3$，$x = 1$ 確實是三重根'
          ],
          answer: '驗證成功：$P(x) = (x-1)^3$'
        },
        hint: '展開完全立方式驗證'
      }
    }
  },
  {
    id: 'M09_12_02',
    topicId: 'M09_12',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '含參數的重根問題：',
    parts: {
      a: {
        question: '求使多項式 $P(x) = x^3 - 3ax^2 + 3a^2x - a^3$ 有重根的參數 $a$ 的值',
        solution: {
          steps: [
            '觀察到 $P(x) = x^3 - 3ax^2 + 3a^2x - a^3$',
            '這個形式類似於 $(x-a)^3$ 的展開',
            '驗證：$(x-a)^3 = x^3 - 3ax^2 + 3a^2x - a^3$',
            '因此 $P(x) = (x-a)^3$',
            '對於任意實數 $a$，$x = a$ 都是三重根'
          ],
          answer: '對於任意實數 $a$，$P(x)$ 都有三重根 $x = a$'
        },
        hint: '識別完全立方式的形式'
      },
      b: {
        question: '求使多項式 $Q(x) = x^3 + px + q$ 有重根的條件',
        solution: {
          steps: [
            '求導：$Q\'(x) = 3x^2 + p$',
            '設 $x = r$ 是重根，則 $Q(r) = 0$ 且 $Q\'(r) = 0$',
            '由 $Q\'(r) = 0$：$3r^2 + p = 0$，得 $r^2 = -\\frac{p}{3}$',
            '代入 $Q(r) = 0$：$r^3 + pr + q = 0$',
            '由 $r^2 = -\\frac{p}{3}$ 得 $r^3 = r \\cdot r^2 = -\\frac{pr}{3}$',
            '代入：$-\\frac{pr}{3} + pr + q = 0$，即 $\\frac{2pr}{3} + q = 0$',
            '結合 $r^2 = -\\frac{p}{3}$，消除 $r$ 得到條件'
          ],
          answer: '重根條件：$4p^3 + 27q^2 = 0$'
        },
        hint: '利用重根的定義建立方程組'
      },
      c: {
        question: '當 $p = -3$，$q = 2$ 時，求 $Q(x) = x^3 - 3x + 2$ 的根',
        solution: {
          steps: [
            '檢查重根條件：$4(-3)^3 + 27(2)^2 = 4(-27) + 27(4) = -108 + 108 = 0$ ✓',
            '由 $Q\'(x) = 3x^2 - 3 = 0$ 得重根 $x^2 = 1$，即 $x = \\pm 1$',
            '檢查：$Q(1) = 1 - 3 + 2 = 0$ ✓，$Q(-1) = -1 + 3 + 2 = 4 \\neq 0$',
            '所以 $x = 1$ 是重根，進行多項式除法：$Q(x) = (x-1)^2(x+2)$',
            '驗證：$Q(-2) = -8 + 6 + 2 = 0$ ✓'
          ],
          answer: '根為 $x = 1$（二重根）和 $x = -2$（單根）'
        },
        hint: '先找重根，再用多項式除法找其他根'
      }
    }
  },
  {
    id: 'M09_12_03',
    topicId: 'M09_12',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '重根的幾何意義：',
    parts: {
      a: {
        question: '分析函數 $f(x) = (x-2)^2(x+1)$ 在 $x = 2$ 處的圖像行為',
        solution: {
          steps: [
            '$x = 2$ 是二重根，函數在此處與 $x$ 軸相切',
            '求導：$f\'(x) = 2(x-2)(x+1) + (x-2)^2 = (x-2)[2(x+1) + (x-2)] = (x-2)(3x)$',
            '$f\'(2) = 0$，確認 $x = 2$ 是駐點',
            '二階導數：$f\'\'(x) = 3(x-2) + 3x = 6x - 6$',
            '$f\'\'(2) = 6 > 0$，所以 $x = 2$ 是局部最小值點',
            '圖像在 $x = 2$ 處與 $x$ 軸相切，不穿越'
          ],
          answer: '在 $x = 2$ 處，函數與 $x$ 軸相切，形成局部最小值'
        },
        hint: '二重根對應圖像與 $x$ 軸相切'
      },
      b: {
        question: '比較 $g(x) = (x-1)^3$ 和 $h(x) = (x-1)^4$ 在 $x = 1$ 處的圖像行為',
        solution: {
          steps: [
            '對於 $g(x) = (x-1)^3$：$x = 1$ 是三重根（奇數重）',
            '$g\'(x) = 3(x-1)^2$，$g\'(1) = 0$',
            '$g\'\'(x) = 6(x-1)$，$g\'\'(1) = 0$',
            '$g\'\'\'(x) = 6$，$g\'\'\'(1) = 6 \\neq 0$，所以 $x = 1$ 是拐點',
            '對於 $h(x) = (x-1)^4$：$x = 1$ 是四重根（偶數重）',
            '$h\'\'(1) = 0$，$h^{(4)}(1) = 24 \\neq 0$，所以 $x = 1$ 是極小值點'
          ],
          answer: '奇數重根對應拐點（穿越 $x$ 軸），偶數重根對應極值點（不穿越 $x$ 軸）'
        },
        hint: '重根的奇偶性決定圖像是否穿越 $x$ 軸'
      },
      c: {
        question: '設計一個五次多項式，使其有一個三重根和一個二重根',
        solution: {
          steps: [
            '設三重根為 $x = a$，二重根為 $x = b$（$a \\neq b$）',
            '多項式形式：$P(x) = c(x-a)^3(x-b)^2$',
            '這是五次多項式：$3 + 2 = 5$',
            '例如：取 $a = 0$，$b = 1$，$c = 1$',
            '$P(x) = x^3(x-1)^2 = x^3(x^2 - 2x + 1) = x^5 - 2x^4 + x^3$'
          ],
          answer: '例如：$P(x) = x^5 - 2x^4 + x^3$，有三重根 $x = 0$ 和二重根 $x = 1$'
        },
        hint: '重根的重數之和等於多項式的次數'
      }
    }
  },
  {
    id: 'M09_12_04',
    topicId: 'M09_12',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '重根與導數的關係：',
    parts: {
      a: {
        question: '證明：$x = a$ 是多項式 $P(x)$ 的 $k$ 重根當且僅當 $P(a) = P\'(a) = \\cdots = P^{(k-1)}(a) = 0$ 且 $P^{(k)}(a) \\neq 0$',
        solution: {
          steps: [
            '充分性：如果 $P(a) = P\'(a) = \\cdots = P^{(k-1)}(a) = 0$ 且 $P^{(k)}(a) \\neq 0$',
            '由泰勒展開：$P(x) = \\frac{P^{(k)}(a)}{k!}(x-a)^k + \\frac{P^{(k+1)}(a)}{(k+1)!}(x-a)^{k+1} + \\cdots$',
            '提取 $(x-a)^k$：$P(x) = (x-a)^k \\cdot Q(x)$，其中 $Q(a) = \\frac{P^{(k)}(a)}{k!} \\neq 0$',
            '必要性：如果 $x = a$ 是 $k$ 重根，則 $P(x) = (x-a)^k Q(x)$，$Q(a) \\neq 0$',
            '逐次求導並在 $x = a$ 處求值，得到前 $k-1$ 階導數為零，第 $k$ 階導數非零'
          ],
          answer: '證明完成：重根的重數等於第一個非零導數的階數'
        },
        hint: '利用泰勒展開和多項式的因式分解'
      },
      b: {
        question: '利用上述結果，求多項式 $P(x) = x^4 - 4x^3 + 6x^2 - 4x + 1$ 在 $x = 1$ 處的重數',
        solution: {
          steps: [
            '$P(1) = 1 - 4 + 6 - 4 + 1 = 0$',
            '$P\'(x) = 4x^3 - 12x^2 + 12x - 4$，$P\'(1) = 4 - 12 + 12 - 4 = 0$',
            '$P\'\'(x) = 12x^2 - 24x + 12$，$P\'\'(1) = 12 - 24 + 12 = 0$',
            '$P\'\'\'(x) = 24x - 24$，$P\'\'\'(1) = 24 - 24 = 0$',
            '$P^{(4)}(x) = 24$，$P^{(4)}(1) = 24 \\neq 0$',
            '因此 $x = 1$ 是四重根'
          ],
          answer: '$x = 1$ 是四重根'
        },
        hint: '逐階求導直到得到非零值'
      },
      c: {
        question: '驗證 $P(x) = x^4 - 4x^3 + 6x^2 - 4x + 1 = (x-1)^4$',
        solution: {
          steps: [
            '展開 $(x-1)^4$ 使用二項式定理：',
            '$(x-1)^4 = \\sum_{k=0}^4 \\binom{4}{k} x^k (-1)^{4-k}$',
            '$= x^4 - 4x^3 + 6x^2 - 4x + 1$',
            '這確實等於給定的多項式',
            '因此 $P(x) = (x-1)^4$，驗證了 $x = 1$ 是四重根'
          ],
          answer: '驗證成功：$P(x) = (x-1)^4$'
        },
        hint: '利用二項式定理展開'
      }
    }
  },
  {
    id: 'M09_12_05',
    topicId: 'M09_12',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '重根的應用：',
    parts: {
      a: {
        question: '設計一個三次多項式，使其有一個二重根 $x = 2$ 和一個單根 $x = -1$',
        solution: {
          steps: [
            '多項式形式：$P(x) = a(x-2)^2(x+1)$',
            '這確實是三次多項式：$2 + 1 = 3$',
            '取 $a = 1$：$P(x) = (x-2)^2(x+1)$',
            '展開：$P(x) = (x^2 - 4x + 4)(x + 1) = x^3 - 3x^2 + 4$',
            '驗證：$P(2) = 0$，$P\'(2) = 0$，$P(-1) = 0$'
          ],
          answer: '$P(x) = x^3 - 3x^2 + 4$'
        },
        hint: '利用因式分解形式構造多項式'
      },
      b: {
        question: '求使方程 $x^3 + px^2 + qx + r = 0$ 有二重根的條件',
        solution: {
          steps: [
            '設二重根為 $\\alpha$，單根為 $\\beta$',
            '多項式可寫為：$(x-\\alpha)^2(x-\\beta) = x^3 - (2\\alpha + \\beta)x^2 + (\\alpha^2 + 2\\alpha\\beta)x - \\alpha^2\\beta$',
            '比較係數：$p = -(2\\alpha + \\beta)$，$q = \\alpha^2 + 2\\alpha\\beta$，$r = -\\alpha^2\\beta$',
            '消除 $\\alpha$ 和 $\\beta$：由前兩個方程得 $\\beta = -p - 2\\alpha$',
            '代入第三個方程並結合第二個方程，得到判別式條件',
            '最終條件：$\\Delta = 18pqr - 4p^3r + p^2q^2 - 4q^3 - 27r^2 = 0$'
          ],
          answer: '二重根條件：$\\Delta = 18pqr - 4p^3r + p^2q^2 - 4q^3 - 27r^2 = 0$'
        },
        hint: '利用韋達定理和判別式'
      },
      c: {
        question: '應用上述條件，判斷方程 $x^3 - 6x^2 + 12x - 8 = 0$ 是否有重根',
        solution: {
          steps: [
            '識別係數：$p = -6$，$q = 12$，$r = -8$',
            '計算判別式：$\\Delta = 18(-6)(12)(-8) - 4(-6)^3(-8) + (-6)^2(12)^2 - 4(12)^3 - 27(-8)^2$',
            '$= 18 \\cdot 576 - 4 \\cdot (-216) \\cdot (-8) + 36 \\cdot 144 - 4 \\cdot 1728 - 27 \\cdot 64$',
            '$= 10368 - 6912 + 5184 - 6912 - 1728 = 0$',
            '由於 $\\Delta = 0$，方程有重根',
            '實際上，$x^3 - 6x^2 + 12x - 8 = (x-2)^3$，有三重根 $x = 2$'
          ],
          answer: '方程有重根，實際上是三重根 $x = 2$'
        },
        hint: '計算三次方程的判別式'
      }
    }
  }

  // 繼續添加其他主題的問題...
];

// 導出模組
export default {
  topics: M09_TOPICS_11_TO_20,
  problems: M09_PROBLEMS_11_TO_20
}; 